import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DeviceService } from './device.service';
import { GlobalSearchService } from './globalsearchservice.service';

export type GroupMode = 'single' | 'split';

/** One credit memo as extracted by the server (apir/docintake/creditmemo). */
export interface CreditMemoFields {
	page: number;
	// 'debit_memo' is the vendor billing the money back (a reversed deal). Amounts still
	// arrive POSITIVE from the extractor — the entry screen applies the sign.
	document_type: 'credit_memo' | 'debit_memo' | 'invoice' | 'other';
	vendor_name: string;
	vendor_remit_to: string;
	customer_name: string;
	customer_account: string;
	customer_ship_to: string;
	program_dealer?: string;   // end dealer from the Notes line — the real customer the credit is for
	credit_memo_number: string;
	source_invoice_ref: string;
	po_number: string;
	invoice_date: string;
	due_date: string;
	total_amount: number;
	commission_amount: number;
	relief_amount: number;
	lines: Array<{ itemcode: string; description: string; qty: number; unit_price: number; extended: number }>;
}

export interface MatchCandidate {
	score: number;
	auto: boolean;
}
export interface VendorMatch extends MatchCandidate { supplierid: string; suppname: string; currcode: string; }
export interface CustomerMatch extends MatchCandidate { debtorno: string; branchcode: string; name: string; }

/** One of the customer's open AR invoices (allocation target). */
export interface InvoiceRow { id: string; transno: string; orderno: string; reference: string; trandate: string; leftto: number; }

/** Which open invoices the credit pays, and how the receipt allocates across them. */
export interface InvoiceSelection {
	selected_ids: string[];
	allocations: { [id: string]: number };
	auto: boolean;
	score: number;
}

export interface ExtractedMemo {
	fields: CreditMemoFields;
	vendor_match: VendorMatch | null;
	customer_match: CustomerMatch | null;
	invoice_selection: InvoiceSelection;
	ar_receipt: number;
	ready: boolean;
	confidence: { vendor: number; customer: number; invoice: number };
}

/** Payment defaults the batch runner resolves once (bank/currency/method/journal + NA GL codes). */
export interface NaPaymentDefaults {
	bankaccount: string;
	currency: string;
	payment_type: string;
	journalno: any;
	reliefGl: string;
	commissionGl: string;
}

export interface ExtractResponse {
	success: boolean;
	message?: string;
	group_mode?: GroupMode;
	memos?: ExtractedMemo[];
	open_invoices_by_customer?: { [debtorno: string]: InvoiceRow[] };
}

/** One vendor purchase invoice as extracted by the server (apir/docintake/vendorinvoice). */
export interface VendorInvoiceFields {
	document_type: 'invoice' | 'credit_memo' | 'other';
	vendor_name: string;
	vendor_remit_to: string;
	invoice_number: string;
	po_numbers: string[];
	invoice_date: string;
	due_date: string;
	discount_date: string;
	discount_amount: number;
	subtotal: number;
	tax_amount: number;
	freight_amount: number;
	fet_amount: number;
	total_amount: number;
	lines: Array<{ itemcode: string; description: string; qty: number; unit_price: number; extended: number }>;
	page?: number;
	raw?: any;   // unprocessed model row, for in-screen inspection
}

export interface VendorInvoiceExtractResponse {
	success: boolean;
	message?: string;
	invoice?: VendorInvoiceFields;
}

/** One invoice read from a batch PDF (docintake/vendorinvoices), with a vendor-name match. */
export interface VendorInvoiceCandidate {
	fields: VendorInvoiceFields & { page?: number };
	vendor_match: VendorMatch | null;
}
export interface VendorInvoicesResponse {
	success: boolean;
	message?: string;
	group_mode?: GroupMode;
	invoices?: VendorInvoiceCandidate[];
}

const SPLIT_PREF_KEY = 'docintake.multipage.mode';

@Injectable({ providedIn: 'root' })
export class DocumentIntakeService {
	private config: any;
	private user: any = [];
	private httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

	constructor(private http: HttpClient, private globalSearchService: GlobalSearchService, private deviceService: DeviceService) {
		this.globalSearchService.configsubscription.subscribe(r => { this.config = r; this.updateHeaders(); });
		this.globalSearchService.user.subscribe(u => { this.user = u; this.updateHeaders(); });
		this.updateHeaders();
	}

	private updateHeaders() {
		const uuid = this.deviceService.getDeviceUUID();
		let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
		if (this.config?.apiCredentials?.clientId && this.user?.user?.userid) {
			headers = headers.set('Authorization', 'Bearer ' + this.config.apiCredentials.clientId + '_' + this.user.user.userid);
		}
		headers = headers.set('X-Device-UUID', uuid);
		this.httpOptions.headers = headers;
	}

	private setEndPoint(method: string): string {
		return this.config.apiServer.crossPoint + method
			+ '&key=' + this.config.apiCredentials.clientId + '&userid=' + this.user.user.userid;
	}

	/** Read a credit-memo PDF and return structured memo(s) + candidate vendor/customer matches. */
	public extractCreditMemo(pdfBase64: string, groupMode: GroupMode): Observable<ExtractResponse> {
		const method = 'docintake/creditmemo';
		const body = { pdf_base64: pdfBase64, group_mode: groupMode };
		return this.http.post<ExtractResponse>(this.setEndPoint(method), body, this.httpOptions).pipe(
			catchError((e: any) => throwError(() => e))
		);
	}

	/** Per-page streaming counterpart of extractCreditMemo — reads one page as a single matched memo. */
	public extractCreditMemoPage(pdfBase64: string, page: number): Observable<{ success: boolean; message?: string; memo?: ExtractedMemo; open_invoices_by_customer?: any }> {
		const method = 'docintake/creditmemopage';
		const body = { pdf_base64: pdfBase64, page };
		return this.http.post<{ success: boolean; message?: string; memo?: ExtractedMemo; open_invoices_by_customer?: any }>(this.setEndPoint(method), body, this.httpOptions).pipe(
			catchError((e: any) => throwError(() => e))
		);
	}

	/**
	 * Build the nationalAccountPayment payload for a memo — the same shape payment.component posts,
	 * assembled from the extraction + resolved defaults so the batch runner can post without the
	 * interactive form. Returns null if not postable.
	 *
	 * PARTIAL APPLICATION: the receipt is sized to what's actually ALLOCATED to invoices (Σ toRows),
	 * not the credit memo's stated total. A $400 allocation against a $1000 invoice posts a $400
	 * receipt and leaves the invoice open at $600 (the rest comes from another vendor). The vendor
	 * relief / commission legs scale by the same fraction so the National Account entry stays
	 * balanced. Apply the full credit and it behaves exactly as before.
	 */
	public buildNaPayment(
		memo: ExtractedMemo,
		defaults: NaPaymentDefaults,
		toRows: Array<{ id: string; leftto: number; allocation: number }>,
		dryRun = false,
	): any | null {
		const f = memo.fields;
		const cm = memo.customer_match;
		const vm = memo.vendor_match;
		if (!cm?.debtorno || !vm?.supplierid || !toRows?.length) { return null; }

		const total = Number(f.total_amount) || 0;
		const commission = Number(f.commission_amount) || 0;
		const fullArReceipt = Number(memo.ar_receipt) || Math.round((total - commission) * 100) / 100;

		// Receipt = the amount actually applied to invoices. Scale the credit memo's relief/commission
		// legs by the applied fraction so a partial application posts a balanced (smaller) entry.
		const applied = Math.round(toRows.reduce((s, r) => s + (Number(r.allocation) || 0), 0) * 100) / 100;
		const arReceipt = applied > 0 ? applied : fullArReceipt;
		const frac = fullArReceipt > 0 ? arReceipt / fullArReceipt : 1;
		const commissionNow = Math.round(commission * frac * 100) / 100;
		const creditAmountNow = Math.round(total * frac * 100) / 100;

		const ref = f.credit_memo_number || f.source_invoice_ref || f.po_number || '';
		const datepaid = f.invoice_date || new Date().toISOString().slice(0, 10);

		return {
			dryrun: dryRun,   // server runs the full post path then rolls back when true
			payment: {
				debtorno: cm.debtorno,
				branchcode: cm.branchcode,
				amount: arReceipt,
				discount: 0,
				bankaccount: defaults.bankaccount,
				currency: defaults.currency,
				payment_type: defaults.payment_type,
				datepaid,
				checkno: ref,
				reference: ref,
				description: `Credit memo ${f.vendor_name || ''} ${ref}`.trim(),
				journalno: defaults.journalno || '',
			},
			national_account: {
				vendor_id: vm.supplierid,
				gl_account: defaults.reliefGl,
				credit_amount: creditAmountNow,
				commission_amount: commissionNow > 0 ? commissionNow : 0,
				commission_gl_account: commissionNow > 0 ? defaults.commissionGl : '',
				vendor_reference: ref,
				invoice_date: f.invoice_date || datepaid,
				due_date: f.due_date || '',
			},
			to: toRows.map(r => ({ id: r.id, leftto: r.leftto, allocation: r.allocation })),
		};
	}

	/** Read a vendor purchase-invoice PDF and return its structured header + line items. */
	public extractVendorInvoice(pdfBase64: string): Observable<VendorInvoiceExtractResponse> {
		const method = 'docintake/vendorinvoice';
		const body = { pdf_base64: pdfBase64 };
		return this.http.post<VendorInvoiceExtractResponse>(this.setEndPoint(method), body, this.httpOptions).pipe(
			catchError((e: any) => throwError(() => e))
		);
	}

	/** Read a (possibly multi-invoice) vendor-invoice PDF in batch → invoices[] + vendor matches. */
	public extractVendorInvoices(pdfBase64: string, groupMode: GroupMode): Observable<VendorInvoicesResponse> {
		const method = 'docintake/vendorinvoices';
		const body = { pdf_base64: pdfBase64, group_mode: groupMode };
		return this.http.post<VendorInvoicesResponse>(this.setEndPoint(method), body, this.httpOptions).pipe(
			catchError((e: any) => throwError(() => e))
		);
	}

	/** Read ONE page of a multi-invoice PDF (the PDF is prompt-cached server-side for cheap per-page calls). */
	public extractVendorInvoicePage(pdfBase64: string, page: number): Observable<{ success: boolean; message?: string; invoice?: VendorInvoiceFields; vendor_match?: VendorMatch | null }> {
		const method = 'docintake/vendorinvoicepage';
		const body = { pdf_base64: pdfBase64, page };
		return this.http.post<any>(this.setEndPoint(method), body, this.httpOptions).pipe(
			catchError((e: any) => throwError(() => e))
		);
	}

	/** Item code normalized for GRN matching: upper-case, alphanumerics only. */
	public normCode(s: any): string {
		return String(s == null ? '' : s).toUpperCase().replace(/[^A-Z0-9]/g, '');
	}

	/**
	 * The maker part-number keys in a code, leading-zero-insensitive. Distributors print a short
	 * brand token + part number ("KY 356264107", "FS 014818"); we store BRAND-partno with a
	 * DIFFERENT prefix (KLY-356264107, DLE-014818, VTG-VT34362, PIR-1993600). The shared part is
	 * the number, so we key on every run of ≥4 digits (and its zero-stripped form, so "014818" and
	 * "14818" still meet). Returns [] when there's no number to key on.
	 */
	public codeKeys(s: any): string[] {
		const runs: string[] = String(s == null ? '' : s).match(/\d{4,}/g) || [];
		const set = new Set<string>();
		runs.forEach(r => { set.add(r); const z = r.replace(/^0+/, ''); if (z) { set.add(z); } });
		return Array.from(set);
	}

	/** Best single display key for an item code (longest digit run), for badges/dedupe. */
	public codeKey(s: any): string {
		const keys = this.codeKeys(s);
		if (!keys.length) { return this.normCode(s); }
		return keys.reduce((a, b) => (b.length > a.length ? b : a));
	}

	/**
	 * Tire-size tokens normalized for matching: 285/45ZR22, P235/70R16, LT275/60R20, 35X12.50R18.
	 * Tire vendors (e.g. VANS) print the size, not our internal code (RAD-DSC0311), and sizes have
	 * no 4-digit run so codeKeys() misses them — this is the join when the codes don't align.
	 */
	public sizeKeys(s: any): string[] {
		const str = String(s == null ? '' : s).toUpperCase();
		const out = new Set<string>();
		const re = /[A-Z]{0,2}\d{2,3}(\/|X)\d{2}(\.\d{1,2})?[A-Z]{0,3}\d{2}/g;
		let m: RegExpExecArray | null;
		while ((m = re.exec(str)) !== null) { out.add(m[0].replace(/[^A-Z0-9]/g, '')); }
		return Array.from(out);
	}

	/**
	 * Pick the open GRN lines that satisfy an extracted invoice, from a vendor's open-GRN pool.
	 * Matches each invoice line to a GRN line by item code; PO-number agreement boosts confidence.
	 * Returns cloned GRN rows (toinvoice defaulted to uninvoiced received qty, unit price taken
	 * from the invoice line) ready to drop into the create() `grns` array — never auto-posted.
	 */
	public matchInvoiceToGrns(fields: VendorInvoiceFields, pool: any[]): { grns: any[]; matchedCodes: string[]; unmatched: string[] } {
		const lines = Array.isArray(fields?.lines) ? fields.lines : [];
		const pos = (fields?.po_numbers || []).map(p => this.normCode(p)).filter(Boolean);

		// Each invoice line's join keys come from the item code AND the description together. Some
		// distributors (e.g. ATD) print their OWN sku in the code column and the maker part number
		// — the one in our stock id — only in the description (e.g. "...STRONG GUARD [*98513*]" →
		// HER-98513). Keying off both catches it.
		//
		// ACCUMULATE split lines: a vendor invoice often lists the SAME item on several lines (e.g.
		// 2 + 4 of one tire) while back office holds a single GRN line for the combined qty (6).
		// Group invoice lines by their best item key, summing qty and using a qty-weighted unit price
		// (so the extended total is preserved), then match the ONE combined want to the ONE GRN line.
		const byKey: { [k: string]: { keys: Set<string>; sizes: Set<string>; code: string; qty: number; extSum: number; raw: string; done: boolean } } = {};
		lines.forEach((l: any) => {
			const keys = new Set([...this.codeKeys(l.itemcode), ...this.codeKeys(l.description)]);
			const sizes = this.sizeKeys((l.itemcode || '') + ' ' + (l.description || ''));
			// Group key: prefer a code, then a tire size, then the normalized text — so codeless tire
			// lines are NOT dropped (they match by PO + size + price below).
			const gk = this.codeKey(l.itemcode) || this.codeKey(l.description) || sizes[0] || this.normCode(l.description) || this.normCode(l.itemcode);
			if (!gk) { return; }
			if (!byKey[gk]) { byKey[gk] = { keys: new Set<string>(), sizes: new Set<string>(), code: '', qty: 0, extSum: 0, raw: (l.itemcode || l.description || '').trim(), done: false }; }
			const entry = byKey[gk];
			keys.forEach(k => entry.keys.add(k));
			sizes.forEach(s => entry.sizes.add(s));
			const ic = this.normCode(l.itemcode);   // alnum stem, e.g. "AGS081" — matches "ARY-AGS081" by suffix
			if (ic.length > entry.code.length) { entry.code = ic; }
			const q = parseFloat(l.qty) || 0;
			const p = parseFloat(l.unit_price) || 0;
			entry.qty += q;
			// Cost basis = the line's MERCHANDISE value (qty × unit_price). Tire vendors fold a line's
			// FET into its `extended` (extended = qty × unit_price + FET), and our entry screen re-adds
			// FET from the stock master — so adopting an extended-derived price double-counts the FET and
			// reads as "over by the FET". Use unit_price when present; fall back to extended only when the
			// line gives no usable unit price.
			entry.extSum += (p > 0) ? q * p : ((l.extended != null && l.extended !== '') ? (parseFloat(l.extended) || 0) : 0);
		});
		const wants = Object.keys(byKey).map(k => {
			const w = byKey[k];
			return { keys: w.keys, sizes: w.sizes, code: w.code, qty: w.qty, price: w.qty > 0 ? w.extSum / w.qty : 0, raw: w.raw, done: false };
		}).filter(w => w.keys.size > 0 || w.sizes.size > 0 || w.code.length >= 5 || w.qty > 0);

		const grns: any[] = [];
		const usedGrnno = new Set<string>();
		// For each invoice line pick the BEST open-GRN candidate. A candidate QUALIFIES on any of:
		// the item code (digit-run key on itemcode OR description), the printed PO/SO# (matched to the
		// GRN's orderno/shiptref/jobref), or the tire SIZE (when a tire vendor prints the size, not our
		// code). Score ranks them: code > PO > size > unit price > qty-left. Nothing is double-claimed.
		for (const want of wants) {
			let best: any = null;
			let bestScore = -1;
			for (const g of (pool || [])) {
				if (usedGrnno.has(String(g.grnno))) { continue; }
				const gkeys = [...this.codeKeys(g.itemcode), ...this.codeKeys(g.itemdescription)];
				const gsizes = this.sizeKeys((g.itemcode || '') + ' ' + (g.itemdescription || ''));
				// Alnum-stem match: invoice prints "AGS081", we store "ARY-AGS081" (brand prefix). The
				// shared stem has no 4-digit run so codeKeys misses it — match by suffix/equality.
				const gc = this.normCode(g.itemcode);
				const alnumHit = want.code.length >= 5 && gc.length >= 5 && (gc === want.code || gc.endsWith(want.code) || want.code.endsWith(gc));
				const codeHit = alnumHit || gkeys.some(k => want.keys.has(k));
				const sizeHit = gsizes.some(s => want.sizes.has(s));
				const gRefs = [g.orderno, g.shiptref, g.jobref].map((x: any) => this.normCode(x)).filter(Boolean);
				const poHit = pos.length > 0 && gRefs.some(r => pos.indexOf(r) >= 0);
				if (!codeHit && !poHit && !sizeHit) { continue; }
				const leftto = Math.max(0, (parseFloat(g.qtyrecd) || 0) - (parseFloat(g.quantityinv) || 0));
				if (leftto <= 0) { continue; }   // fully invoiced — nothing left to bill against
				const priceHit = want.price > 0 && Math.abs((parseFloat(g.unitprice) || 0) - want.price) <= 0.01;
				const qtyOk = want.qty > 0 && leftto >= want.qty;
				const score = (codeHit ? 1000 : 0) + (poHit ? 800 : 0) + (sizeHit ? 400 : 0) + (priceHit ? 100 : 0) + (qtyOk ? 10 : 0);
				if (score > bestScore) { bestScore = score; best = g; }
			}
			if (!best) { continue; }
			grns.push(this.grnRowForInvoice(best, want.qty, want.price));
			usedGrnno.add(String(best.grnno));
			want.done = true;
		}
		const matchedCodes = wants.filter(w => w.done).map(w => w.raw);
		const unmatched = wants.filter(w => !w.done).map(w => w.raw);
		return { grns, matchedCodes, unmatched };
	}

	/** Shape a pooled GRN row into a selected invoice line: cap qty at the invoice's, take its price. */
	public grnRowForInvoice(g: any, invoiceQty?: number, invoicePrice?: number): any {
		const row = { ...g };
		const recd = parseFloat(g.qtyrecd) || 0;
		const alreadyInv = parseFloat(g.quantityinv) || 0;
		const leftto = Math.max(0, recd - alreadyInv) || recd;
		row.toinvoice = (invoiceQty && invoiceQty > 0) ? Math.min(invoiceQty, leftto) : leftto;
		// Track whether qty came off the invoice or was defaulted to the GRN's open qty, and how much
		// is open — the screen warns when we're billing fewer units than were received (a mis-read qty
		// strands units in GRN suspense even though the dollar total still reconciles).
		row.qtyFromInvoice = !!(invoiceQty && invoiceQty > 0);
		row.qtyOpen = leftto;
		row.additionaldesc = '';
		row.poUnitPrice = parseFloat(g.unitprice) || 0;
		if (invoicePrice != null && invoicePrice > 0) { row.unitprice = invoicePrice; }
		return row;
	}

	/**
	 * Totals for a vendor invoice, mirroring the interactive entry screen's getSubTotal/getTaxTotal:
	 * subtotal = Σ(unit price × qty); each vendor tax = rate × subtotal (summed); FET (tires only) =
	 * Σ(fet × qty); total = subtotal + tax + fet − discount. Mutates the passed vendor.taxes[].taxamount
	 * (create()/glpreview() read it), so pass a CLONE per row.
	 */
	public computeVendorInvoiceTotals(vendor: any, grns: any[], discount: number, isTires: boolean): { subtotal: number; taxes: number; fet: number; total: number } {
		const subtotal = (grns || []).reduce((s, g) => s + (parseFloat(g.unitprice) || 0) * (parseFloat(g.toinvoice) || 0), 0);
		let taxes = 0;
		if (Array.isArray(vendor?.taxes)) {
			vendor.taxes.forEach((t: any) => {
				const amt = (parseFloat(t.taxrate) || 0) * subtotal;
				t.taxamount = amt;
				taxes += amt;
			});
		}
		const fet = isTires ? (grns || []).reduce((s, g) => s + (parseFloat(g.fetcharge) || 0) * (parseFloat(g.toinvoice) || 0), 0) : 0;
		const total = subtotal + taxes + fet - (discount || 0);
		return {
			subtotal: Math.round(subtotal * 100) / 100,
			taxes: Math.round(taxes * 100) / 100,
			fet: Math.round(fet * 100) / 100,
			total: Math.round(total * 100) / 100,
		};
	}

	/**
	 * Assemble the apir/vendor/invoice payload (same shape the entry screen posts) for dry-run
	 * (glpreview) or live post (create). Clones the vendor so tax mutation can't bleed across rows.
	 */
	public buildVendorInvoicePayload(vendor: any, fields: VendorInvoiceFields, grns: any[], discount: number, isTires: boolean, user: any): any {
		const v = JSON.parse(JSON.stringify(vendor || {}));
		// Totals (incl. tax on the gross subtotal) are unchanged — the discount still nets out of the
		// invoice total. What changes is WHERE the discount lands: instead of an invoice-level discount
		// the posting never costs to inventory (the server reads header.discountAmount only for the
		// example payload — it's never GL'd), we push it DOWN onto the GRN lines, lowering each tire's
		// unit price so every line's posted cost / purchase-price-variance carries its share.
		const totals = this.computeVendorInvoiceTotals(v, grns, discount, isTires);
		const postingGrns = this.allocateDiscountToLines(grns, discount);
		const today = new Date().toISOString().slice(0, 10);
		return {
			vendor: v,
			header: {
				reference: fields.invoice_number || '',
				invoiceDate: fields.invoice_date || today,
				discountAmount: 0,   // allocated per line below — NOT posted as a header discount
				discountDate: fields.discount_date || fields.due_date || today,
				dueDate: fields.due_date || today,
				rate: v.exrate,
				currcode: v.currcode || '',
				grns: '',
				glsearch: '',
				tag: null,
			},
			grns: postingGrns,
			glcharges: [],
			subtotal: totals.subtotal,
			taxes: totals.taxes,
			fet: totals.fet,
			total: totals.total,
			user,
		};
	}

	/**
	 * Spread an invoice-level discount across the GRN lines, proportional to each line's extended
	 * value, by lowering its unit price. Returns CLONED rows (the originals keep the as-read price for
	 * display) with the discount baked in, so the AP posting costs the discount into each line's
	 * purchase-price variance instead of a single invoice-total discount the GL never allocates. The
	 * last line absorbs the rounding remainder so Σ(reduction) == discount to the cent.
	 */
	public allocateDiscountToLines(grns: any[], discount: number): any[] {
		const rows = (grns || []).map(g => ({ ...g }));
		const disc = Math.round((Number(discount) || 0) * 100) / 100;
		if (disc <= 0 || !rows.length) { return rows; }
		const ext = rows.map(g => (parseFloat(g.unitprice) || 0) * (parseFloat(g.toinvoice) || 0));
		const base = ext.reduce((s, e) => s + e, 0);
		if (base <= 0) { return rows; }   // zero-value lines — nothing to spread across
		let allocated = 0;
		rows.forEach((g, i) => {
			const qty = parseFloat(g.toinvoice) || 0;
			const isLast = i === rows.length - 1;
			// Each line's cut of the discount (last line takes the rounding remainder).
			const share = isLast
				? Math.round((disc - allocated) * 100) / 100
				: Math.round((disc * (ext[i] / base)) * 100) / 100;
			allocated += share;
			if (qty > 0) {
				const newUnit = (parseFloat(g.unitprice) || 0) - share / qty;
				g.unitprice = Math.round(newUnit * 1e6) / 1e6;   // keep cost precision; total reconciles to the cent
			}
		});
		return rows;
	}

	/**
	 * Match already-structured memo rows (CSV/Excel import) — same engine, no AI/PDF.
	 * vendorId locks the backing vendor for every row; customer locks the file-level customer
	 * (brand-portal exports are per-customer) — both fill rows the per-memo reference doesn't resolve.
	 */
	public matchMemos(rows: any[], vendorId?: string, customer?: { debtorno: string; branchcode?: string }): Observable<ExtractResponse> {
		const method = 'docintake/matchmemos';
		const body: any = { memos: rows };
		if (vendorId) { body.vendor_id = vendorId; }
		if (customer?.debtorno) { body.debtorno = customer.debtorno; body.branchcode = customer.branchcode || ''; }
		return this.http.post<ExtractResponse>(this.setEndPoint(method), body, this.httpOptions).pipe(
			catchError((e: any) => throwError(() => e))
		);
	}

	/** Search customers for the manual match picker. */
	public searchCustomers(keywords: string): Observable<any> {
		return this.http.post<any>(this.setEndPoint('docintake/searchcustomers'), { keywords }, this.httpOptions)
			.pipe(catchError((e: any) => throwError(() => e)));
	}

	/** The picked customer's open AR invoices — for completing a row the name-matcher left unmatched. */
	public customerInvoices(debtorno: string): Observable<any> {
		return this.http.post<any>(this.setEndPoint('docintake/customerinvoices'), { debtorno }, this.httpOptions)
			.pipe(catchError((e: any) => throwError(() => e)));
	}

	/** File → base64 (no data-URL prefix). */
	public fileToBase64(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const res = String(reader.result || '');
				const comma = res.indexOf(',');
				resolve(comma >= 0 ? res.slice(comma + 1) : res);
			};
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(file);
		});
	}

	/**
	 * Best-effort page count from the raw PDF bytes — enough to decide whether to ask
	 * split-vs-single. The page tree's /Count is authoritative; fall back to counting
	 * /Type /Page objects, then to 1. (Vendor-generated memos are uncompressed enough
	 * that this is reliable; a miss only changes whether the dialog appears.)
	 */
	public async getPdfPageCount(file: File): Promise<number> {
		try {
			const buf = await file.arrayBuffer();
			const bytes = new Uint8Array(buf);
			let s = '';
			for (let i = 0; i < bytes.length; i++) { s += String.fromCharCode(bytes[i]); }
			let max = 0;
			const countRe = /\/Count\s+(\d+)/g;
			let m: RegExpExecArray | null;
			while ((m = countRe.exec(s)) !== null) {
				const n = parseInt(m[1], 10);
				if (n > max) { max = n; }
			}
			if (max > 0) { return max; }
			const pageRe = /\/Type\s*\/Page[^s]/g;
			const pages = (s.match(pageRe) || []).length;
			return pages > 0 ? pages : 1;
		} catch {
			return 1;
		}
	}

	/**
	 * Handoff stash: the batch screen drops a memo here, then routes to the receivables
	 * payment screen, which picks it up on init and pre-fills the NA form. One mapping path,
	 * no complex router state. Consumed once.
	 */
	private pendingMemo: ExtractedMemo | null = null;
	public stashMemo(memo: ExtractedMemo): void { this.pendingMemo = memo; }
	public takeMemo(): ExtractedMemo | null {
		const m = this.pendingMemo;
		this.pendingMemo = null;
		return m;
	}

	/** Remembered split/single preference (mirrors BizSys MultiPagePreferSplit). */
	public getSplitPref(): GroupMode | null {
		const v = (() => { try { return localStorage.getItem(SPLIT_PREF_KEY); } catch { return null; } })();
		return v === 'split' || v === 'single' ? v : null;
	}
	public setSplitPref(mode: GroupMode): void {
		try { localStorage.setItem(SPLIT_PREF_KEY, mode); } catch { /* ignore */ }
	}
	public clearSplitPref(): void {
		try { localStorage.removeItem(SPLIT_PREF_KEY); } catch { /* ignore */ }
	}
}
